import { useQuery } from '@apollo/client';

import { query } from '@/lib/ApolloClient';
import { AllContentBlocks, AuthorFields } from '@/lib/snippets/Blocks';
import { FieldType, strapiGql, strapiGqlFull } from '@/lib/StrapiGqlHelpers';
import { ResourceArticle, ResourceCategory, ResourceCenter } from '@/types';
export type ResourceCenterContent = Pick<
  ResourceCenter,
  'Title' | 'Intro' | 'BlocksAboveCategories' | 'BlocksBelowCategories'
>;

export const getResourceCenterContent = async (draftModeEnabled: boolean = false) => {
  const pageQuery = strapiGql(
    'resourceCenter',
    {
      Title: FieldType.Text,
      Intro: FieldType.Text,
      BlocksAboveCategories: AllContentBlocks,
      BlocksBelowCategories: AllContentBlocks,
    },
    draftModeEnabled
  );

  return await query<{ resourceCenter: ResourceCenterContent }>({
    query: pageQuery,
  });
};

export type ResourceCategorySummary = Pick<
  ResourceCategory,
  'CategoryName' | 'Slug' | 'CategoryHero' | 'Order' | 'publishedAt' | 'updatedAt'
>;
export const getAllResourceCategorySummaries = async (draftModeEnabled: boolean = false) => {
  const pageQuery = strapiGql(
    'resourceCategories',
    {
      CategoryName: FieldType.Text,
      Slug: FieldType.Text,
      CategoryHero: FieldType.Image,
      Order: FieldType.Number,
      publishedAt: FieldType.Text,
      updatedAt: FieldType.Text,
    },
    draftModeEnabled
  );
  return await query<{ resourceCategories: ResourceCategorySummary[] }>({
    query: pageQuery,
  });
};

export type ResourceResultType = Pick<
  ResourceArticle,
  'ResourceTitle' | 'Slug' | 'ResourceDescription' | 'ResourceThumbnail' | 'ResourceCategory'
>;
const publishDateSort = ['OverridePublishDate:desc', 'publishedAt:desc'];
const getCategoriesFromSlugs = (slugs: string[]) =>
  slugs.map((slug = '') => (slug.startsWith('/') ? slug.slice(1).trim() : slug.trim()));

export const getLatestResources = async (
  count: number,
  resourceCategorySlugs?: string[],
  draftModeEnabled: boolean = false
) => {
  const categories = resourceCategorySlugs?.length
    ? getCategoriesFromSlugs(resourceCategorySlugs)
    : null;

  // Build filters dynamically
  const filters = `
    IncludeInLatestBlock: { eq: true }
    ${
      categories
        ? `
      ResourceCategory: {
        Slug: { in: ${JSON.stringify(categories)} }
      }
    `
        : ''
    }
  `;

  const pageQuery = strapiGqlFull(
    'resourceArticles',
    {
      ResourceTitle: FieldType.Text,
      Slug: FieldType.Text,
      HeroImage: FieldType.Image,
      Author: AuthorFields,
      OverridePublishDate: FieldType.Text,
      ShowDatePublished: FieldType.Boolean,
      ShowLastUpdated: FieldType.Boolean,
      publishedAt: FieldType.Text,
      updatedAt: FieldType.Text,
      ResourceDescription: FieldType.Text,
      ResourceThumbnail: FieldType.Image,
      ResourceCategory: {
        CategoryName: FieldType.Text,
        Slug: FieldType.Text,
      },
    },
    draftModeEnabled,
    `{ ${filters} }`,
    publishDateSort,
    {
      page: 0,
      pageSize: count,
    }
  );

  return await query<{ resourceArticles: ResourceResultType[] }>({
    query: pageQuery,
  });
};

export const getResourceCategoryContent = async (slug: string, draftModeEnabled: boolean) => {
  const pageQuery = strapiGql(
    'resourceCategories',
    {
      CategoryName: FieldType.Text,
      Slug: FieldType.Text,
      CategoryIntro: FieldType.Text,
      CategoryHero: FieldType.Image,
      Order: FieldType.Number,
      ResourceArticles: {
        ResourceTitle: FieldType.Text,
        Slug: FieldType.Text,
        ResourceDescription: FieldType.Text,
        ResourceThumbnail: FieldType.Image,
        Author: AuthorFields,
        OverridePublishDate: FieldType.Text,
        ShowDatePublished: FieldType.Boolean,
        ShowLastUpdated: FieldType.Boolean,
        publishedAt: FieldType.Text,
        updatedAt: FieldType.Text,
      },
      Blocks: AllContentBlocks,
    },
    draftModeEnabled,
    slug
  );
  return await query<{ resourceCategories: ResourceCategory[] }>({
    query: pageQuery,
  });
};

export const useGetResourcesSearchQuery = (
  searchTerm: string,
  resourceCategorySlugs: string[] = [],
  page = 0,
  pageSize = 20,
  draftModeEnabled: boolean = false,
  skip: boolean = false
) => {
  const categories = getCategoriesFromSlugs(resourceCategorySlugs);
  const hasSearchTerm = searchTerm.trim().length > 0;

  // Build filters: if search term exists, use search filters; otherwise just category filter
  const filters = hasSearchTerm
    ? `{
      or: [
        { ResourceTitle: { containsi: "${searchTerm}" } },
        { ResourceDescription: { containsi: "${searchTerm}" } },
        { ResourceIntro: { containsi: "${searchTerm}" } },
      ]
      ${categories.length > 0 ? `ResourceCategory: { Slug: { in: ${JSON.stringify(categories)} } }` : ''}
    }`
    : categories.length > 0
      ? `{ ResourceCategory: { Slug: { in: ${JSON.stringify(categories)} } } }`
      : '{}';

  const pageQuery = strapiGqlFull(
    'resourceArticles',
    {
      ResourceTitle: FieldType.Text,
      Slug: FieldType.Text,
      ResourceDescription: FieldType.Text,
      ResourceThumbnail: FieldType.Image,
      Author: AuthorFields,
      OverridePublishDate: FieldType.Text,
      ShowDatePublished: FieldType.Boolean,
      ShowLastUpdated: FieldType.Boolean,
      publishedAt: FieldType.Text,
      updatedAt: FieldType.Text,
      ResourceCategory: {
        CategoryName: FieldType.Text,
        Slug: FieldType.Text,
      },
    },
    draftModeEnabled,
    filters,
    publishDateSort,
    { page, pageSize }
  );
  return useQuery(pageQuery, {
    skip,
    // Use client-side Apollo client
    fetchPolicy: 'cache-and-network',
  });
};

export const resourcArticlesAttributes = {
  Author: AuthorFields,
  HeroImage: FieldType.Image,
  OverridePublishDate: FieldType.Text,
  ResourceCategory: {
    CategoryName: FieldType.Text,
    Slug: FieldType.Text,
  },
  ResourceDescription: FieldType.Text,
  ResourceIntro: FieldType.Text,
  ResourceThumbnail: FieldType.Image,
  ResourceTitle: FieldType.Text,
  ShowDatePublished: FieldType.Boolean,
  ShowLastUpdated: FieldType.Boolean,
  Slug: FieldType.Text,
  ArticleExplainer: {
    Title: FieldType.Text,
    Items: {
      BoldText: FieldType.Text,
      Text: FieldType.Text,
    },
  },
  publishedAt: FieldType.Text,
  updatedAt: FieldType.Text,
  seo: {
    metaTitle: FieldType.Text,
    metaDescription: FieldType.Text,
    metaRobots: FieldType.Text,
    metaViewport: FieldType.Text,
    canonicalURL: FieldType.Text,
    structuredData: FieldType.Text,
    metaImage: FieldType.Image,
  },
  Sidebar: {
    ShowJumpLinks: FieldType.Boolean,
    LeadFunnel: {
      FunnelLogo: FieldType.Image,
      FunnelHeader: FieldType.Text,
      FunnelSubHeader: FieldType.Text,
      FunnelVariant: FieldType.Text,
      FunnelFootNote: FieldType.Text,
      FunnelContents: FieldType.Blocks,
      FunnelHideInnerBackground: FieldType.Boolean,
      InputLabel: FieldType.Text,
      InputPlaceholder: FieldType.Text,
      InputLink: FieldType.Text,
      FunnelButtonLabel: FieldType.Text,
    },
    ArticleSidebarCta: {
      Title: FieldType.Text,
      Items: {
        BoldText: FieldType.Text,
        Text: FieldType.Text,
      },
      ButtonText: FieldType.Text,
      ButtonUrl: FieldType.Text,
      PhoneImage: FieldType.Image,
      PhoneNumber: FieldType.Text,
      ShowShareLinks: FieldType.Boolean,
      ShowShareLabels: FieldType.Boolean,
      ShareMail: FieldType.Boolean,
      ShareFacebook: FieldType.Boolean,
      ShareInstagram: FieldType.Boolean,
      ShareX: FieldType.Boolean,
      ShareLinkedIn: FieldType.Boolean,
    },
    AuthorCard: {
      ShowBio: FieldType.Boolean,
      ShowSocialLabels: FieldType.Boolean,
      WrittenBy: {
        Name: FieldType.Text,
        Avatar: FieldType.Image,
        Role: FieldType.Text,
        Bio: FieldType.Text,
        LinkedInUrl: FieldType.Text,
        Email: FieldType.Text,
        FacebookUrl: FieldType.Text,
        InstagramUrl: FieldType.Text,
        XUrl: FieldType.Text,
        createdAt: FieldType.Text,
        updatedAt: FieldType.Text,
        publishedAt: FieldType.Text,
      },
    },
    MetricHighlight: {
      MetricHighlightTitle: FieldType.Text,
      MetricHighlightInfo: {
        MetricDescription: FieldType.Text,
        MetricInfo: FieldType.Text,
      },
    },
  },
  PopupFunnel: FieldType.Boolean,
  FunnelPopupTimer: FieldType.Number,
  NoIndex: FieldType.Boolean,
  Blocks: AllContentBlocks,
};

export const getResourceBySlugQuery = async (slug: string, draftModeEnabled: boolean = false) => {
  const pageQuery = strapiGql(
    'resourceArticles',
    resourcArticlesAttributes,
    draftModeEnabled,
    slug
  );
  return await query<{ resourceArticles: ResourceArticle[] }>({
    query: pageQuery,
  });
};
